import type { User } from '@/types/api';
import { apiClientMethods } from '../lib/api-client';
import { clearAuthData, clearAuthAndRedirect } from '../utils/authUtils';

export const PENDING_VERIFICATION_EMAIL_KEY = 'pending_verification_email';
export const PENDING_VERIFICATION_PHONE_KEY = 'pending_verification_phone';
export const PENDING_VERIFICATION_REQUIRES_PHONE_KEY = 'pending_verification_requires_phone';
export const VERIFICATION_RESEND_AVAILABLE_AT_KEY = 'verification_resend_available_at';
export const VERIFICATION_CHALLENGE_ID_KEY = 'verification_challenge_id';
export const VERIFICATION_RESEND_COOLDOWN_SECONDS = 60;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  user: User;
}

export interface EmailVerificationRequiredResponse {
  message: string;
  verificationRequired: true;
  verificationChallengeId?: string;
  userId: string;
  email: string;
}

export type LoginResponse = AuthResponse | EmailVerificationRequiredResponse;
export type LoginResult = 'authenticated' | 'verification_required';

export function isEmailVerificationRequiredResponse(
  response: LoginResponse
): response is EmailVerificationRequiredResponse {
  return 'verificationRequired' in response && response.verificationRequired === true;
}

export interface SignupCredentials {
  businessName: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  physicalAddress?: string;
  email: string;
  phoneNumber: string;
  password: string;
  isAdultConfirmed?: boolean;
  acceptedTerms?: boolean;
  termsVersion?: string;
}

export interface SignupResponse {
  message: string;
  userId: string;
  email?: string;
  phoneNumber?: string;
  verificationRequired: boolean;
  codeChannel?: string;
}

export interface ResendVerificationResponse {
  message: string;
  email?: string;
  phoneNumber?: string;
  codeChannel?: string;
}

export class AuthService {
  /**
   * Register a new merchant account
   */
  async signup(credentials: SignupCredentials): Promise<SignupResponse> {
    try {
      const response = await apiClientMethods.post<SignupResponse>('/auth/signup', credentials);

      if (response.verificationRequired) {
        localStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, credentials.email.trim());
        localStorage.setItem(
          VERIFICATION_RESEND_AVAILABLE_AT_KEY,
          String(Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000)
        );
        localStorage.removeItem(VERIFICATION_CHALLENGE_ID_KEY);
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify email address with verification token
   */
  async verifyEmail(token: string): Promise<AuthResponse> {
    try {
      const payload = { token: token.trim() };
      const response = await apiClientMethods.post<AuthResponse>('/auth/verify-email', payload);

      // Store token and user data after successful verification
      localStorage.setItem('auth_token', response.accessToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
      localStorage.removeItem(PENDING_VERIFICATION_PHONE_KEY);
      localStorage.removeItem(PENDING_VERIFICATION_REQUIRES_PHONE_KEY);
      localStorage.removeItem(VERIFICATION_RESEND_AVAILABLE_AT_KEY);
      localStorage.removeItem(VERIFICATION_CHALLENGE_ID_KEY);

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request a new verification OTP (email, sms, or all).
   */
  async resendVerification(
    email: string,
    verificationChallengeId?: string,
    channel?: 'all' | 'email' | 'sms'
  ): Promise<ResendVerificationResponse> {
    try {
      const response = await apiClientMethods.post<ResendVerificationResponse>(
        '/auth/resend-verification',
        { email, challengeId: verificationChallengeId, channel }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Authenticate user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClientMethods.post<LoginResponse>('/auth/login', credentials);
      if (isEmailVerificationRequiredResponse(response)) {
        clearAuthData();
        localStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, response.email);
        localStorage.setItem(
          VERIFICATION_RESEND_AVAILABLE_AT_KEY,
          String(Date.now() + VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000)
        );
        if (response.verificationChallengeId) {
          localStorage.setItem(VERIFICATION_CHALLENGE_ID_KEY, response.verificationChallengeId);
        }
        return response;
      }

      // Store token and user data
      localStorage.setItem('auth_token', response.accessToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      return response;
    } catch (error) {
      // Clear any existing auth data on login failure
      clearAuthData();
      throw error;
    }
  }

  /**
   * Log out the current user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint to invalidate token on server
      await apiClientMethods.post('/auth/logout');
    } catch (error) {
      // Even if server logout fails, clear local data
      console.warn('Server logout failed, clearing local data anyway:', error);
    } finally {
      clearAuthData();
    }
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    try {
      const user = await apiClientMethods.get<User>('/auth/me');

      // Update stored user data
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch (error) {
      // If getting current user fails, clear auth data
      clearAuthData();
      throw error;
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    return !!token;
  }

  /**
   * Get stored user data without API call
   */
  getStoredUser(): User | null {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to parse stored user data:', error);
      clearAuthData();
      return null;
    }
  }

  /**
   * Get stored auth token
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Refresh the authentication token
   */
  async refreshToken(): Promise<string> {
    try {
      const response = await apiClientMethods.post<{ token: string }>('/auth/refresh');

      // Update stored token
      localStorage.setItem('auth_token', response.token);

      return response.token;
    } catch (error) {
      // If refresh fails, clear auth data and redirect to login
      clearAuthAndRedirect();
      throw error;
    }
  }

  /**
   * Request a password reset OTP.
   * User provides their email; OTP is sent to their stored phone number.
   */
  async forgotPassword(email: string): Promise<{ message: string; maskedPhone?: string }> {
    try {
      const response = await apiClientMethods.post<{ message: string; maskedPhone?: string }>(
        '/auth/forgot-password',
        { email }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reset password using OTP received on the stored phone number.
   * Requires the user's email (used to identify the account) + the OTP + the new password.
   */
  async resetPassword(data: {
    email: string;
    otp: string;
    password: string;
  }): Promise<{ message: string }> {
    try {
      const response = await apiClientMethods.post<{ message: string }>(
        '/auth/reset-password',
        data
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
